import type { Rectangle } from "@glideapps/glide-data-grid";

export interface RowResizeTarget {
  sheetId: string;
  row: number;
  pixels: number;
}

const POINTS_TO_PIXELS = 96 / 72;
export const pixelHeightToPoints = (pixels: number) =>
  pixels / POINTS_TO_PIXELS;
export const pointHeightToPixels = (points: number) =>
  Math.max(1, Math.round(points * POINTS_TO_PIXELS));

const ROW_BOUNDARY_TOLERANCE_PX = 4;
// Floor the pixel limit so converting back to points never exceeds 409.
const MAX_ROW_HEIGHT_PX = Math.floor(409 * POINTS_TO_PIXELS);

export const clampRowHeight = (height: number, minimum: number) =>
  Math.min(MAX_ROW_HEIGHT_PX, Math.max(minimum, Math.round(height)));

export const findRowResizeBoundary = ({
  x,
  y,
  rowCount,
  visibleRows,
  getBounds,
}: {
  x: number;
  y: number;
  rowCount: number;
  visibleRows: Pick<Rectangle, "y" | "height">;
  getBounds: (row: number) => Rectangle | undefined;
}): number | null => {
  const header = getBounds(-1);
  if (!header || y < header.y + header.height) return null;
  if (x < header.x || x > header.x + header.width) return null;

  // Glide's backwards bounds for hidden rows can overlap visible rows when
  // heights differ. Only search rows from its current visible region.
  let low = Math.max(0, visibleRows.y);
  let high = Math.min(rowCount - 1, visibleRows.y + visibleRows.height - 1);
  while (low <= high) {
    const row = Math.floor((low + high) / 2);
    const bounds = getBounds(row);
    if (!bounds) return null;
    const bottom = bounds.y + bounds.height;
    if (Math.abs(y - bottom) <= ROW_BOUNDARY_TOLERANCE_PX) return row;
    if (row > 0 && Math.abs(y - bounds.y) <= ROW_BOUNDARY_TOLERANCE_PX) {
      return row - 1;
    }
    if (y < bounds.y) high = row - 1;
    else if (y > bottom) low = row + 1;
    else return null;
  }
  return null;
};
